import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import UploadMediaModal from '../components/(admin)/UploadMediaModal'
import { 
  fetchMedia,
  uploadMedia,
  clearError,
  clearSuccess
} from '../store/slices/mediaSlice'
import NotificationModal from '../components/NotificationModal'
import { resolveMediaUrl } from '../utils/mediaUrl'

const MediaLibrary = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { media: rawMedia, loading, error, success } = useSelector((state) => state.media)
  
  const [filteredMedia, setFilteredMedia] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [fileTypeFilter, setFileTypeFilter] = useState('all')
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [isViewing, setIsViewing] = useState(false)

  // Notification modal state
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  })

  useEffect(() => {
    dispatch(fetchMedia())
  }, [dispatch])

  useEffect(() => {
    filterMedia()
  }, [searchQuery, fileTypeFilter, rawMedia])

  // Show notifications for errors and success
  useEffect(() => {
    if (error) {
      showNotification('error', 'Error', error)
      dispatch(clearError())
    }
    if (success) {
      showNotification('success', 'Success', success)
      dispatch(clearSuccess())
      dispatch(fetchMedia()) // Refresh media list
    }
  }, [error, success, dispatch])

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap()
      navigate('/signin')
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  const filterMedia = () => {
    let filtered = [...(rawMedia || [])]

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.original_name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    // Filter by file type
    if (fileTypeFilter !== 'all') {
      filtered = filtered.filter(item => {
        const mimeType = item.mime_type || ''
        if (fileTypeFilter === 'image') {
          return mimeType.startsWith('image/')
        } else if (fileTypeFilter === 'video') {
          return mimeType.startsWith('video/')
        } else if (fileTypeFilter === 'audio') {
          return mimeType.startsWith('audio/')
        } else if (fileTypeFilter === 'document') {
          return mimeType.startsWith('application/') || mimeType.startsWith('text/')
        }
        return true
      })
    }

    setFilteredMedia(filtered)
  }

  const handleUpload = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    dispatch(uploadMedia(formData))
  }

  const handleView = (mediaItem) => {
    setSelectedMedia(mediaItem)
    setIsViewing(true)
  }

  const handleCloseView = () => {
    setIsViewing(false)
    setSelectedMedia(null)
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const getFileType = (mimeType) => {
    if (!mimeType) return 'Unknown'
    if (mimeType.startsWith('image/')) return 'Image'
    if (mimeType.startsWith('video/')) return 'Video'
    if (mimeType.startsWith('audio/')) return 'Audio'
    if (mimeType.startsWith('application/') || mimeType.startsWith('text/')) return 'Document'
    return 'Other'
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0
  }

  const searchBarStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap'
  }

  const searchInputStyle = {
    flex: 1,
    minWidth: '200px',
    padding: '10px 16px',
    fontSize: '14px',
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    backgroundColor: colors.background.primary,
    color: colors.text.primary
  }

  const selectStyle = {
    padding: '10px 16px',
    fontSize: '14px',
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    cursor: 'pointer',
    minWidth: '150px'
  }

  const uploadButtonStyle = {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s ease'
  }

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '20px'
  }

  const cardStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  }

  const thumbnailStyle = {
    width: '100%',
    height: '200px',
    objectFit: 'contain',
    backgroundColor: colors.background.secondary
  }

  const cardContentStyle = {
    padding: '16px'
  }

  const fileNameStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }

  const tagStyle = {
    display: 'inline-block',
    padding: '4px 8px',
    fontSize: '12px',
    fontWeight: '500',
    color: colors.primary,
    backgroundColor: '#EFF6FF',
    borderRadius: '4px',
    marginBottom: '8px'
  }

  const fileSizeStyle = {
    fontSize: '12px',
    color: colors.text.secondary,
    marginBottom: '8px'
  }

  const urlContainerStyle = {
    marginBottom: '12px',
    padding: '8px',
    backgroundColor: colors.background.secondary,
    borderRadius: '4px',
    border: `1px solid ${colors.borderVariants.light}`
  }

  const urlLabelStyle = {
    fontSize: '10px',
    fontWeight: '600',
    color: colors.text.secondary,
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  }

  const urlTextStyle = {
    fontSize: '11px',
    color: colors.primary,
    wordBreak: 'break-all',
    fontFamily: 'monospace',
    cursor: 'pointer',
    textDecoration: 'underline',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  }

  const actionButtonsStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  }

  const actionButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    color: colors.text.secondary,
    transition: 'color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  const viewModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1001
  }

  const viewModalStyle = {
    maxWidth: '90vw',
    maxHeight: '90vh',
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    padding: '24px',
    position: 'relative'
  }

  const closeButtonStyle = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: colors.text.primary,
    cursor: 'pointer',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)'
  }

  const viewImageStyle = {
    maxWidth: '100%',
    maxHeight: '80vh',
    objectFit: 'contain'
  }

  if (loading) {
    return (
      <div style={containerStyle}>
        <Sidebar />
        <div style={mainContentStyle}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading media...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <Sidebar />

      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="Media Library"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <button
            style={uploadButtonStyle}
            onClick={() => setIsModalOpen(true)}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colors.primary
            }}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            Upload Media
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div style={searchBarStyle}>
          <input
            type="text"
            placeholder="Search"
            style={searchInputStyle}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            style={selectStyle}
            value={fileTypeFilter}
            onChange={(e) => setFileTypeFilter(e.target.value)}
          >
            <option value="all">File Type</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
            <option value="document">Document</option>
          </select>
        </div>

        {/* Media Grid */}
        {error ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.status.error }}>
            <div>Error: {error}</div>
            <button
              onClick={() => dispatch(fetchMedia())}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        ) : filteredMedia.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.text.secondary }}>
            {rawMedia.length === 0 ? 'No media found. Upload your first media file!' : 'No media matches your filters.'}
          </div>
        ) : (
          <div style={gridStyle}>
            {filteredMedia.map((item) => (
              <div key={item.id} style={cardStyle}>
                {item.mime_type?.startsWith('image/') ? (
                  <img
                    src={resolveMediaUrl(item.url)}
                    alt={item.original_name}
                    style={thumbnailStyle}
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                ) : (
                  <div style={{ ...thumbnailStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="64" height="64" fill={colors.text.secondary} viewBox="0 0 24 24">
                      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                    </svg>
                  </div>
                )}
                <div style={cardContentStyle}>
                  <div style={fileNameStyle}>{item.original_name}</div>
                  <div style={tagStyle}>{getFileType(item.mime_type)}</div>
                  <div style={fileSizeStyle}>{formatFileSize(item.file_size)}</div>
                  
                  {/* URL Display */}
                  <div style={urlContainerStyle}>
                    <div style={urlLabelStyle}>Public URL</div>
                    <a
                      href={resolveMediaUrl(item.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={urlTextStyle}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={(e) => {
                        e.target.style.color = colors.primaryDark
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = colors.primary
                      }}
                      title="Click to open in new tab"
                    >
                      {resolveMediaUrl(item.url)}
                      <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18 19H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h5c.55 0 1-.45 1-1s-.45-1-1-1H5c-1.11 0-2 .89-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-6c0-.55-.45-1-1-1s-1 .45-1 1v5c0 .55-.45 1-1 1zM14 4c0 .55.45 1 1 1h2.59l-9.13 9.13c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L19 6.41V9c0 .55.45 1 1 1s1-.45 1-1V4c0-.55-.45-1-1-1h-5c-.55 0-1 .45-1 1z" />
                      </svg>
                    </a>
                  </div>

                  <div style={actionButtonsStyle}>
                    <button
                      style={actionButtonStyle}
                      onClick={() => handleView(item)}
                      title="View"
                      onMouseEnter={(e) => {
                        e.target.style.color = colors.primary
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = colors.text.secondary
                      }}
                    >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <UploadMediaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={handleUpload}
      />

      {/* View Modal */}
      {isViewing && selectedMedia && (
        <div style={viewModalOverlayStyle} onClick={handleCloseView}>
          <div style={viewModalStyle} onClick={(e) => e.stopPropagation()}>
            <button style={closeButtonStyle} onClick={handleCloseView}>
              ×
            </button>
            {selectedMedia.mime_type?.startsWith('image/') ? (
              <img src={resolveMediaUrl(selectedMedia.url)} alt={selectedMedia.original_name} style={viewImageStyle} />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p style={{ color: colors.text.primary, marginBottom: '16px' }}>
                  {selectedMedia.original_name}
                </p>
                <a
                  href={resolveMediaUrl(selectedMedia.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: colors.primary,
                    textDecoration: 'underline'
                  }}
                >
                  Open in new tab
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  )
}

export default MediaLibrary

